import React, { FC, memo, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { addToast } from '~actions';
import { useDispatch, useI18n } from '~Hooks';

export const Wrapper = styled.div<{ disabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
  text-decoration: none;
  color: #333;
  height: 100%;
  transition: all 0.2s ease-out;
  ${({ disabled }) => disabled && 'opacity: 0.5'}
`;

// Square icon
const NavigationButton = styled.div<{
  touching: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  width: 80px;
  height: 80px;
  background: #fff;
  margin-bottom: 8px;
  border-radius: 12px;
  position: relative;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  ${({ touching }) =>
    touching &&
    css`
      transform: scale(0.98);
      box-shadow: 0px 1px 18px 2px rgba(255, 255, 255, 0.8);
    `}
`;

const IconTitle = styled.span`
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.5px;
  color: #fbfbfb;
  display: block;
`;

interface Props {
  title: string;
  icon: JSX.Element;
  background: string;
  disabled: boolean;
  onClick: () => void;
}

const NavButton: FC<Props> = ({ title, icon, background, disabled, onClick }) => {
  const { i18n } = useI18n();
  const dispatch = useDispatch();

  const [isNavigating, setIsNavigating] = useState<boolean>(false);
  const [touching, setTouching] = useState<boolean>(false);

  const handleClick = () => {
    if (disabled) {
      dispatch(addToast(i18n('General.MissingPermission'), true));
      return;
    }
    setIsNavigating(true);

    setTimeout(onClick, 250);
  };

  return (
    <Wrapper
      disabled={disabled}
      onClick={handleClick}
      onTouchStart={() => setTouching(true)}
      onTouchEnd={() => setTouching(false)}>
      <NavigationButton
        style={{
          background,
        }}
        touching={touching || isNavigating}>
        {icon}
      </NavigationButton>
      <IconTitle>{title}</IconTitle>
    </Wrapper>
  );
};

export default memo(NavButton, (prevProps, nextProps) => {
  return (
    prevProps.title === nextProps.title &&
    prevProps.icon === nextProps.icon &&
    prevProps.background === nextProps.background &&
    prevProps.disabled === nextProps.disabled
  );
});
